/**
 * Reconciliación al retomar: qué hacer con el estado de las fotos que devuelve el backend.
 *
 * El veredicto de calidad llega en vivo por Pusher, sin replay: si la app estaba cerrada, se pierde.
 * Al retomar se le pregunta al backend por los veredictos persistidos y se aplica lo que falta.
 *
 * Contrato del endpoint (`GET inspection/photos/state` del Gateway):
 *   - devuelve **veredictos persistidos**, no declaraciones: que un archivo no esté en la lista no
 *     dice nada de si se declaró, así que acá nunca se borra ni se marca nada por ausencia;
 *   - `status: null` es una fila sin veredicto y tampoco dice nada;
 *   - 401 es la OI vencida o el token inválido; 502, 503 y 504 son fallos transitorios. Ninguno de
 *     los dos invalida nada local.
 */
import { locateAnalyzedFile } from './PhotoAnalysisMatch';
import { QUALITY_WARNING_FALLBACK } from './QualityWarnings';
import { ANALYSIS_FAILED_MESSAGE } from './PendingDegradation';

export const PHOTO_STATE_ENDPOINT = 'inspection/photos/state';

/**
 * Cuánto se espera la consulta. Las dos esperas bloquean al asegurado, así que son distintas a
 * propósito:
 *   - al entrar a la inspección no hay feedback en pantalla: si el backend tarda, se sigue sin
 *     reconciliar, el degradado corre igual y la próxima vez se reintenta;
 *   - al cerrar la espera queda bajo el spinner, y el aviso de calidad lee los errores al hacer clic:
 *     necesita los reconciliados.
 */
export const PHOTO_STATE_TIMEOUT_ON_RESUME_MS = 3_000;
export const PHOTO_STATE_TIMEOUT_ON_CLOSE_MS = 10_000;

export type ServerPhotoStatus = 'success' | 'error' | 'analysis_failed';

export interface ServerPhotoState {
  key_path: string;
  photo_id: number;
  status: ServerPhotoStatus | null;
}

export type StateFetchOutcome =
  | { kind: 'ok'; items: ServerPhotoState[]; dropped: number }
  | { kind: 'expired' }
  | { kind: 'unavailable'; detail: string };

export interface ReconcilableEntry {
  photoId: number;
  files: { uuid?: string; data?: unknown; analysisResult?: { status?: string } }[];
}

export interface ReconciliationVerdict {
  key: string;
  photoId: number;
  fileIdx: number;
  status: ServerPhotoStatus;
  messages: string[];
}

const SERVER_STATUSES: ReadonlySet<unknown> = new Set(['success', 'error', 'analysis_failed']);

function isServerPhotoState(item: unknown): item is ServerPhotoState {
  const candidate = item as Partial<ServerPhotoState> | null;
  return (
    !!candidate &&
    typeof candidate.key_path === 'string' &&
    candidate.key_path !== '' &&
    Number.isInteger(candidate.photo_id) &&
    (candidate.status === null || SERVER_STATUSES.has(candidate.status))
  );
}

/** `httpStatus` es `null` cuando no hubo respuesta: red caída o timeout. */
export function classifyStateResponse(httpStatus: number | null, body: unknown): StateFetchOutcome {
  if (httpStatus === 401) {
    return { kind: 'expired' };
  }
  if (httpStatus !== 200) {
    return { kind: 'unavailable', detail: httpStatus === null ? 'sin respuesta' : `HTTP ${httpStatus}` };
  }
  const list = (body as { PhotoAnalysisState?: unknown } | null)?.PhotoAnalysisState;
  if (!Array.isArray(list)) {
    return { kind: 'unavailable', detail: 'respuesta sin PhotoAnalysisState' };
  }
  const items = list.filter(isServerPhotoState);
  return { kind: 'ok', items, dropped: list.length - items.length };
}

/**
 * El mensaje que acompaña al veredicto. El endpoint no trae los motivos —no se persisten—, así que
 * un `error` reconciliado lleva el mismo texto de respaldo que el aviso del cierre, y un
 * `analysis_failed`, el mismo que pone el degradado por tiempo.
 */
function messagesFor(status: ServerPhotoStatus): string[] {
  if (status === 'error') return [QUALITY_WARNING_FALLBACK];
  if (status === 'analysis_failed') return [ANALYSIS_FAILED_MESSAGE];
  return [];
}

/**
 * Qué veredictos aplicar. **No muta nada.**
 *
 * - `corrected` no se toca, igual que en el camino en vivo: es una acción del asegurado.
 * - Un estado local igual al del servidor no se reescribe, así se conservan los motivos que llegaron
 *   por Pusher, que el endpoint no tiene.
 * - Ante ambigüedad no se elige: una key que casa con más de un archivo, o que llega dos veces con
 *   veredictos distintos, se ignora.
 */
export function planReconciliation(
  entries: ReconcilableEntry[],
  items: ServerPhotoState[]
): ReconciliationVerdict[] {
  const byKey = new Map<string, Set<ServerPhotoStatus>>();
  for (const item of items) {
    if (item.status === null) continue;
    const statuses = byKey.get(item.key_path) ?? new Set<ServerPhotoStatus>();
    statuses.add(item.status);
    byKey.set(item.key_path, statuses);
  }

  const plan: ReconciliationVerdict[] = [];
  for (const [key, statuses] of byKey) {
    if (statuses.size !== 1) continue;
    const [status] = [...statuses];
    const match = locateAnalyzedFile(entries, { key });
    if (!match.found) continue;
    const entry = entries.find((candidate) => candidate.photoId === match.photoId);
    const current = entry?.files[match.fileIdx]?.analysisResult?.status;
    if (current === 'corrected' || current === status) continue;
    plan.push({ key, photoId: match.photoId, fileIdx: match.fileIdx, status, messages: messagesFor(status) });
  }
  return plan;
}
